<script setup lang="ts">
/**
 * This component is used for general product selection. It's globally registered and may
 * also be used by apps.
 * 
 * It's more general and less strict in nature than the order-specific product selector component.
 */


import { apps } from "@hantera/portal-app";
import { DropdownMenu, InlineInput } from "@hantera/design-system";
import { nextTick, ref, useTemplateRef, watch } from "vue";
import ProductSearchHit from "./ProductSearchHit.vue";
import useApps from "@/composition/use-apps";

const props = defineProps<{
  disabled?: boolean
  allowEmpty?: boolean
}>()

const model = defineModel<string, string, string, string>()

const isEditing = ref(false)
const textInput = useTemplateRef<InstanceType<typeof InlineInput>>('textInput')
const productNumber = ref<string>();
let searchDebouncer: ReturnType<typeof setTimeout> | undefined;
const searchHits = ref<apps.standardServices.SearchProduct[]>([]);
const selectedHit = ref<apps.standardServices.SearchProduct | undefined>(undefined);
const appsContext = useApps();
const searchHitsDropdown = useTemplateRef<InstanceType<typeof DropdownMenu>>('searchHitsDropdown')
const editorElem = useTemplateRef<HTMLDivElement>('editorElem')

const displayFallbackImage = ref(true)

const product = ref<{
  image?: string
  number: string
  description?: string
} | undefined>()

function onImageError() {
  displayFallbackImage.value = true
}

watch(model, async () => {

  product.value = {
    number: model.value ?? ''
  }

  if (product.value.number) {
    const services = appsContext.getServices(apps.standardServices.productsService);
    for (const s of services) {
      const result = await s.lookup(product.value.number)

      if (result) {
        product.value = {
          description: result.description,
          image: result.image,
          number: result.productNumber
        }
        break;
      }
    }
  }

}, { immediate: true })

watch(() => product.value?.image, () => {
  displayFallbackImage.value = false
})

function edit() {
  productNumber.value = model.value ?? ''

  nextTick(() => {
    isEditing.value = true;
    nextTick(() => {
      textInput.value?.focus();
      textInput.value?.select();
    })
  })
}

watch(productNumber, () => {
  if (!isEditing.value) {
    return
  }

  clearTimeout(searchDebouncer)

  searchDebouncer = setTimeout(search, 200);
})

async function search() {
  if (!isEditing.value) {
    return;
  }

  if (!productNumber.value) {
    return []
  }

  const services = appsContext.getServices(apps.standardServices.productsService);

  const products: apps.standardServices.SearchProduct[] = [];

  for (const s of services) {
    products.unshift(...await s.search(
      productNumber.value!
    ))
  }

  searchHits.value = products;
}

function cancel() {
  isEditing.value = false
  searchHits.value = []
}

function select(hit?: apps.standardServices.SearchProduct) {
  selectedHit.value = hit;
}

async function confirm() {

  if (props.allowEmpty && !productNumber.value) {
    model.value = undefined
    isEditing.value = false
    searchHits.value = []
    return
  }

  if (!selectedHit.value) {
    isEditing.value = false
    searchHits.value = []
    return
  }

  if (selectedHit.value) {
    model.value = selectedHit.value.productNumber
  } else {
    model.value = productNumber.value
  }

  isEditing.value = false
  searchHits.value = []
}

function keydown(event: KeyboardEvent) {

  if (!isEditing.value || searchHits.value.length === 0) {
    return;
  }

  if (event.key === 'ArrowDown') {
    if (!selectedHit.value) {
      selectedHit.value = searchHits.value[0];
      event.preventDefault()
      return;
    }

    const currentIdx = searchHits.value.findIndex(r => r.productNumber === selectedHit.value?.productNumber);
    if (currentIdx < searchHits.value.length - 1) {
      selectedHit.value = searchHits.value[currentIdx + 1]
      event.preventDefault()
    }
  }
  if (event.key === 'ArrowUp') {
    if (!selectedHit.value) {
      return;
    }

    const currentIdx = searchHits.value.findIndex(r => r.productNumber === selectedHit.value?.productNumber);
    if (currentIdx > 0) {
      selectedHit.value = searchHits.value[currentIdx - 1]
      event.preventDefault()
    }
  }
  if (event.key === 'Enter') {
    confirm();
  }
  if (event.key === 'Escape') {
    cancel();
  }
}

watch(() => searchHits.value.length, () => {
  if (!searchHitsDropdown.value) {
    return
  }

  if (searchHits.value.length > 0) {

    searchHitsDropdown.value.open()
  } else {
    searchHitsDropdown.value.close()
  }
})

</script>

<template>
  <div class="product-selector">
    <div class="product-image box" v-if="displayFallbackImage">

    </div>
    <a :href="product.image" target="_blank" class="product-image box" v-else-if="product">
      <img
        :src="product.image"
        @error="onImageError"
      />
    </a>
  
    <button class="btn-link product-select" v-if="!isEditing && !disabled" @click="edit">
      <span class="product-select-label" :title="model ?? 'Select product...'">
        {{ model && model.length > 0 ? model : 'Select product...' }}
      </span>
    </button>
    <div v-else-if="!disabled" class="editor" ref="editorElem">
      <InlineInput ref="textInput" v-model="productNumber" @keydown.stop="keydown" />
      <button class="btn-link" @click="cancel">Cancel</button>
    </div>
    <DropdownMenu ref="searchHitsDropdown" :target="editorElem" :popover="true" v-if="!disabled">
      <ProductSearchHit
        v-for="hit in searchHits"
        :key="hit.productNumber"
        :hit="hit"
        @mouseenter="select(hit)"
        @click="confirm"
        :class="{
          'search-hit': true,
          'search-hit--selected': selectedHit?.productNumber === hit.productNumber
        }"
      />
    </DropdownMenu>
    <div v-if="disabled" class="product-number">
      {{ model }}
    </div>

    <div class="product-description" :title="product?.description">
      {{ product?.description }}
    </div>
  </div>
</template>

<style scoped>
.product-selector {
  display: grid;
  grid-template-columns: 40px auto;
  grid-template-rows: auto auto;
  align-items: center;
  column-gap: 5px;
}

.product-image {
  grid-row: 1 / 3;
  height: 40px;
}

.product-image img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.editor {
  display: grid;
  grid-template-columns: auto max-content;
  align-items: center;
  gap: 5px;
}

.product-select {
  text-align: left;
  display: flex;
  justify-content: flex-start;
}

.product-select-label {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.search-hit--selected {
  background-color: hsl(var(--color-primary));
  color: white;
}

.product-description {
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
}
</style>